<template>
  <master-layout>
    <!-- Block Ionic swipe-back gesture -->
    <div class="swipe-back-blocker" @touchstart.stop @touchmove.stop></div>

    <!-- Scanner overlay -->
    <div v-if="scanning" class="scanner-overlay">
      <div class="swipe-back-blocker" @touchstart.stop @touchmove.stop></div>
      <div class="scanner-mask"></div>
      <div class="scanner-frame"></div>
      <!-- Back button -->
      <button class="scanner-back-btn" @click="exitScanMode">
        <ion-icon :icon="arrowBackOutline" class="text-[1.5rem] text-white"></ion-icon>
      </button>
      <div class="scanner-bottom-ui">
        <p class="text-white text-[1rem] font-semibold text-center">Scan attendee's QR code</p>
        <!-- Recipients pills on scanner -->
        <div v-if="recipients.length > 0" class="flex flex-wrap justify-center gap-2 mt-4 px-4">
          <span
            v-for="r in recipients"
            :key="r.personalId"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-[0.8125rem] font-semibold text-white"
          >
            {{ r.personalId }}
            <button class="active:opacity-50" @click="removeRecipient(r.personalId)">
              &times;
            </button>
          </span>
        </div>
        <!-- Upload progress on scanner -->
        <div v-if="uploadProgress" class="mt-4">
          <p class="text-white/80 text-[0.875rem] text-center">{{ uploadProgress }}</p>
        </div>
        <!-- Done button -->
        <button
          v-if="recipients.length > 0"
          class="mt-6 px-6 py-2.5 rounded-full bg-white text-[#3D3A35] text-[0.875rem] font-semibold active:opacity-70"
          @click="exitScanMode"
        >
          Done
        </button>
      </div>
    </div>

    <div
      v-show="!scanning"
      class="px-5 pb-[40px] flex flex-col min-h-full"
      style="padding-top: calc(env(safe-area-inset-top, 24px) + 12px)"
    >
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <BackButton compact disableSafeArea />
      </div>

      <h1 class="text-[2rem] leading-[2.5rem] font-black text-[#42362B] tracking-[-0.03em] mb-6">
        PhotoDrop Upload
      </h1>

      <!-- Auth checking -->
      <div v-if="!authChecked" class="flex items-center justify-center py-20">
        <ion-spinner name="crescent" class="text-[#3D3A35] w-[32px] h-[32px]"></ion-spinner>
      </div>

      <!-- Not authorized -->
      <div v-else-if="!authorized" class="flex flex-col items-center justify-center py-20">
        <ion-icon :icon="lockClosedOutline" class="text-[3rem] text-[#D1D5DB] mb-4"></ion-icon>
        <p class="text-[1.125rem] font-semibold text-[#42362B] mb-1">Not Authorized</p>
        <p class="text-[1rem] text-[#6B6B6B] text-center mb-6">
          You're not authorized as a photographer.
        </p>
        <button
          class="h-[48px] px-8 bg-[#3D3A35] text-white rounded-[36px] text-[1rem] font-semibold active:opacity-80"
          @click="$router.go(-1)"
        >
          Go Back
        </button>
      </div>

      <!-- Authorized — main UI -->
      <template v-else>
        <!-- Step 1: Select photos -->
        <div v-if="mode === 'select'">
          <p class="text-[1rem] text-[#6B6B6B] mb-4">Select up to 20 photos to upload</p>

          <!-- Photo grid preview -->
          <div v-if="selectedPhotos.length > 0" class="mb-6">
            <div class="grid grid-cols-4 gap-[2px]">
              <div
                v-for="(photo, index) in visiblePhotos"
                :key="index"
                class="relative aspect-square overflow-hidden rounded-lg"
              >
                <img :src="photo.webPath" class="w-full h-full object-cover" />
                <!-- Overflow badge on last visible photo -->
                <div
                  v-if="index === visiblePhotos.length - 1 && overflowCount > 0"
                  class="absolute inset-0 bg-black/50 flex items-center justify-center"
                >
                  <span class="text-white text-[1.25rem] font-bold">+{{ overflowCount }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between mt-3">
              <p class="text-[0.875rem] text-[#6B6B6B]">
                {{ selectedPhotos.length }} photo{{ selectedPhotos.length === 1 ? '' : 's' }}
                selected
              </p>
              <button
                class="text-[0.875rem] font-semibold text-[#3D3A35] active:opacity-70"
                @click="triggerFilePicker"
              >
                Change Photos
              </button>
            </div>
          </div>

          <!-- Select button -->
          <button
            v-if="selectedPhotos.length === 0"
            class="w-full py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold active:opacity-80"
            @click="triggerFilePicker"
          >
            Select from Gallery
          </button>

          <!-- Method buttons (shown after photos selected) -->
          <div v-if="selectedPhotos.length > 0" class="flex flex-col gap-3 mt-4">
            <button
              class="w-full py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold active:opacity-80 flex items-center justify-center gap-2"
              @click="enterScanMode"
            >
              <ion-icon :icon="scanOutline" class="text-[1.25rem]"></ion-icon>
              Scan &amp; Send
            </button>
            <button
              class="w-full py-[16px] bg-[#EEECE8] text-[#3D3A35] rounded-full text-[1rem] font-semibold active:opacity-80 flex items-center justify-center gap-2"
              @click="startCodeMode"
            >
              <ion-icon :icon="keypadOutline" class="text-[1.25rem]"></ion-icon>
              Generate Code
            </button>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="onFilesSelected"
          />
        </div>

        <!-- Code mode -->
        <div v-if="mode === 'code'">
          <!-- Uploading -->
          <div v-if="codeUploading" class="flex flex-col items-center justify-center py-20">
            <ion-spinner
              name="crescent"
              class="text-[#3D3A35] w-[32px] h-[32px] mb-4"
            ></ion-spinner>
            <p class="text-[1rem] text-[#6B6B6B]">{{ uploadProgress || 'Generating code...' }}</p>
          </div>

          <!-- Code display -->
          <div v-else-if="generatedCode" class="flex flex-col items-center pt-4">
            <p class="text-[1rem] text-[#6B6B6B] text-center mb-8">
              Attendees scan this QR or enter the code in PhotoDrop &rarr; Receive
            </p>
            <div
              class="bg-white rounded-[20px] p-10 flex items-center justify-center shadow-lg mb-6"
            >
              <qrcode-vue :value="generatedCode" :size="200" level="H" />
            </div>
            <p class="text-[3rem] font-black text-[#42362B] tracking-[0.2em] mb-2">
              {{ generatedCode }}
            </p>
            <p class="text-[0.875rem] text-[#6B6B6B] mb-10">
              {{ selectedPhotos.length }} photo{{ selectedPhotos.length === 1 ? '' : 's' }} uploaded
            </p>
            <button
              class="w-full py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold active:opacity-80"
              @click="resetAll"
            >
              Upload More Photos
            </button>
          </div>
        </div>

        <!-- Scan success summary -->
        <div v-if="mode === 'scanDone'">
          <div class="flex flex-col items-center justify-center py-10">
            <div
              class="w-[64px] h-[64px] bg-[#E8F5E9] rounded-full flex items-center justify-center mb-6"
            >
              <ion-icon
                :icon="checkmarkCircleOutline"
                class="text-[2rem] text-[#4CAF50]"
              ></ion-icon>
            </div>
            <h2 class="text-[1.5rem] font-bold leading-[2rem] text-center mb-2">
              Sent to {{ recipients.length }} {{ recipients.length === 1 ? 'person' : 'people' }}
            </h2>
            <p class="text-[0.875rem] text-[#6B6B6B] mb-2">
              {{ selectedPhotos.length }} photo{{ selectedPhotos.length === 1 ? '' : 's' }} uploaded
            </p>
            <div class="flex flex-wrap justify-center gap-2 mt-4 mb-8">
              <span
                v-for="r in recipients"
                :key="r.personalId"
                class="inline-flex items-center px-3 py-1.5 rounded-full bg-[#E8F5E9] text-[0.8125rem] font-semibold text-[#2E7D32]"
              >
                {{ r.personalId }}
              </span>
            </div>
            <button
              class="w-full py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold active:opacity-80"
              @click="resetAll"
            >
              Upload More Photos
            </button>
          </div>
        </div>
      </template>
    </div>
  </master-layout>
</template>

<script>
import { mapState } from 'vuex';
import { toastController } from '@ionic/vue';
import {
  scanOutline,
  keypadOutline,
  arrowBackOutline,
  lockClosedOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import QrcodeVue from 'qrcode.vue';
import { triggerNotification } from '@/utils/haptics';
import BackButton from '@/components/common/BackButton.vue';

const ENCRYPTED_TOKEN_REGEX = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const MAX_PHOTOS = 20;

export default {
  name: 'PhotographerApp',
  components: { BackButton, QrcodeVue },
  data() {
    return {
      mode: 'select', // 'select' | 'code' | 'scanDone'
      scanning: false,
      photoKeys: [],
      generatedCode: null,
      codeUploading: false,
      uploadProgress: null,
      uploadedCount: 0,
    };
  },
  computed: {
    ...mapState('photographer', ['authorized', 'authChecked', 'selectedPhotos', 'recipients']),
    visiblePhotos() {
      return this.selectedPhotos.slice(0, 8);
    },
    overflowCount() {
      return Math.max(0, this.selectedPhotos.length - 8);
    },
  },
  async ionViewWillEnter() {
    // Disable swipe-back gesture on this page
    const outlet = document.querySelector('ion-router-outlet');
    if (outlet) outlet.swipeGesture = false;

    await this.$store.dispatch('photographer/checkAccess');
  },
  ionViewWillLeave() {
    this.stopScan();
    // Re-enable swipe-back gesture when leaving
    const outlet = document.querySelector('ion-router-outlet');
    if (outlet) outlet.swipeGesture = true;
  },
  created() {
    Object.assign(this, {
      scanOutline,
      keypadOutline,
      arrowBackOutline,
      lockClosedOutline,
      checkmarkCircleOutline,
    });
  },
  methods: {
    // --- Toast ---
    async showToast(message, color = '#08A045') {
      try {
        await toastController.dismiss();
      } catch {
        /* none active */
      }
      const toast = await toastController.create({
        message,
        duration: 2500,
        position: 'bottom',
        cssClass: 'photographer-toast',
        mode: 'ios',
      });
      const shadowRoot = toast.shadowRoot;
      if (shadowRoot) {
        const style = document.createElement('style');
        style.textContent = `.toast-wrapper { background: ${color} !important; --background: ${color} !important; color: #fff !important; }`;
        shadowRoot.appendChild(style);
      }
      toast.style.zIndex = '99999';
      await toast.present();
    },

    // --- File selection ---
    triggerFilePicker() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;
      if (files.length > MAX_PHOTOS) {
        this.showToast(`Maximum ${MAX_PHOTOS} photos allowed`, '#EC4E20');
        return;
      }
      const photos = files.map(file => ({
        uri: URL.createObjectURL(file),
        webPath: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        file,
      }));
      this.$store.commit('photographer/setSelectedPhotos', photos);
    },

    // --- S3 Upload helper ---
    async uploadPhotosToS3(presignedUrls) {
      this.uploadedCount = 0;
      const total = presignedUrls.length;
      for (let i = 0; i < presignedUrls.length; i++) {
        this.uploadProgress = `Uploading ${i + 1}/${total}...`;
        const file = this.selectedPhotos[i]?.file;
        if (!file) continue;
        await fetch(presignedUrls[i], {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });
        this.uploadedCount = i + 1;
      }
      this.uploadProgress = null;
    },

    // --- Scan & Send ---
    async enterScanMode() {
      try {
        const status = await BarcodeScanner.checkPermission({ force: true });
        if (!status.granted) {
          this.showToast('Camera permission is required to scan QR codes.', '#EC4E20');
          return;
        }
        this.scanning = true;
        this.photoKeys = [];
        this.$store.commit('photographer/clearRecipients');
        document.querySelector('body').classList.add('scanner-active');
        const ionApp = document.querySelector('ion-app');
        if (ionApp) ionApp.classList.add('scanner-active');
        // Awaited (#1226): unawaited, a rejection here escapes this try/catch
        // as an unhandled promise rejection, so the webview is left transparent
        // with no toast and no cleanup. The catch below is the path for it.
        await BarcodeScanner.hideBackground();
        this.scanLoop();
      } catch {
        this.cleanupScanner();
        this.showToast('Scanner error. Please try again.', '#EC4E20');
      }
    },
    async scanLoop() {
      if (!this.scanning) return;
      try {
        const result = await BarcodeScanner.startScan();
        if (!this.scanning) return;
        if (result.hasContent) {
          await this.handleScanResult(result.content);
        }
        // Auto-restart scanner for next person
        if (this.scanning) {
          this.scanLoop();
        }
      } catch {
        if (this.scanning) {
          this.cleanupScanner();
          this.showToast('Scanner error. Please try again.', '#EC4E20');
        }
      }
    },
    async handleScanResult(content) {
      // Check if it's an encrypted token
      if (!ENCRYPTED_TOKEN_REGEX.test(content)) {
        this.showToast('Invalid QR code', '#EC4E20');
        return;
      }

      try {
        // Decode the QR token
        const decoded = await this.$store.dispatch('photographer/decodeQr', content);
        const { personalId, memberId } = decoded;

        // Dedup check
        if (this.recipients.some(r => r.personalId === personalId)) {
          this.showToast('Already added', '#F59E0B');
          return;
        }

        // First scan — need to upload photos
        if (this.photoKeys.length === 0) {
          this.showToast(`Sending to ${personalId}...`, '#08A045');
          await triggerNotification('success');
          this.$store.commit('photographer/addRecipient', { personalId, memberId });

          // Get presigned URLs and upload
          const result = await this.$store.dispatch('photographer/getPresignedUrls', {
            numberOfImages: this.selectedPhotos.length,
            personalIds: [personalId],
          });
          this.photoKeys = result.photoKeys;
          await this.uploadPhotosToS3(result.presignedUrls);
          this.showToast(`Sent to ${personalId}`, '#08A045');
        } else {
          // Subsequent scans — just associate
          this.$store.commit('photographer/addRecipient', { personalId, memberId });
          await this.$store.dispatch('photographer/associatePhotos', {
            photoKeys: this.photoKeys,
            personalIds: [personalId],
          });
          await triggerNotification('success');
          this.showToast(`Sent to ${personalId}`, '#08A045');
        }
      } catch (e) {
        console.error('[Photographer] scan error:', e);
        const msg = e.response?.data?.message || 'Failed to process QR code';
        this.showToast(msg, '#EC4E20');
      }
    },
    removeRecipient(personalId) {
      this.$store.commit('photographer/removeRecipient', personalId);
    },
    exitScanMode() {
      this.stopScan();
      if (this.recipients.length > 0 && this.photoKeys.length > 0) {
        this.mode = 'scanDone';
      }
    },
    cleanupScanner() {
      document.querySelector('body').classList.remove('scanner-active');
      const ionApp = document.querySelector('ion-app');
      if (ionApp) ionApp.classList.remove('scanner-active');
      BarcodeScanner.showBackground();
      this.scanning = false;
    },
    stopScan() {
      if (this.scanning) {
        BarcodeScanner.stopScan();
        this.cleanupScanner();
      }
    },

    // --- Generate Code ---
    async startCodeMode() {
      this.mode = 'code';
      this.codeUploading = true;
      this.generatedCode = null;

      try {
        const result = await this.$store.dispatch('photographer/uploadWithCode', {
          numberOfImages: this.selectedPhotos.length,
        });
        this.photoKeys = result.photoKeys;
        this.generatedCode = result.code;
        await this.uploadPhotosToS3(result.presignedUrls);
      } catch (e) {
        console.error('[Photographer] code mode error:', e);
        this.showToast('Failed to generate code. Please try again.', '#EC4E20');
        this.mode = 'select';
      } finally {
        this.codeUploading = false;
      }
    },

    // --- Reset ---
    resetAll() {
      this.mode = 'select';
      this.photoKeys = [];
      this.generatedCode = null;
      this.uploadProgress = null;
      this.uploadedCount = 0;
      this.$store.commit('photographer/resetSession');
    },
  },
};
</script>

<style scoped>
.swipe-back-blocker {
  position: fixed;
  top: 0;
  left: 0;
  width: 20px;
  height: 100%;
  z-index: 9999;
}
</style>

<style>
/* Scanner styles — shared with photodrop */
.photographer-toast {
  --border-radius: 8px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  --start: 16px;
  --end: 16px;
  font-weight: 500;
  font-size: 16px;
}
</style>
